import { useLayoutEffect, useState } from "react"
import console from "./console"
import { useE, useM, useR } from "./hooks"
import { createSubscriptions } from './subscriptions'
import { anyFields, fields, functionOrOther, key, pass, transform } from './types'

type callback<T> = (value: T, oldValue?: T)=> unknown
type cleanup = () => void
type indicator = unknown

type _baseOptions<T> = { value?: T, add?: callback<T> }
type useOptions<T> = functionOrOther<callback<T>, _baseOptions<T>>
type _useValueOptions<T,U> = _baseOptions<T> & { default?: T, defaulter?: transform<[],T>, as?: (value: T)=>U }
type useValueOptions<T> = functionOrOther<callback<T>, _useValueOptions<T,T>>
type useValueAsOptions<T,U> = functionOrOther<transform<T,U>, _useValueOptions<T,U>>

interface _Trigger<T> {
  name?: string
  add: (trigger: callback<T>) => cleanup
  remove: (trigger: callback<T>) => void
  trigger: (value?: T, oldValue?: T) => void
  use: (options?: useOptions<T>) => void,
}
export interface Trigger<T> extends _Trigger<T> {
  once: (trigger: callback<T>) => cleanup
}

interface _KeyedTrigger<T> {
  add: (key: key, trigger: callback<T>) => cleanup
  remove: (key: key, trigger: callback<T>) => void
  trigger: (key: key, value?: T, oldValue?: T) => void
  single: (key: key) => Trigger<T>,
  use: (key: key, options?: useOptions<T>) => void,
}
export interface KeyedTrigger<T> extends _KeyedTrigger<T> {
  once: (key: key, trigger: callback<T>) => cleanup
}

interface _TriggerValue<T> extends _Trigger<T> {
  value: T
  get: () => T,
  set: (value?: T) => cleanup,
  add: (trigger: callback<T>, initialize?: indicator) => cleanup,
  use: (options?: useValueOptions<T>) => [T, (value?: T)=>void],
  as: <U,>(options?: useValueAsOptions<T,U>) => [U, (value?: T)=>void],
}
export interface TriggerValue<T> extends _TriggerValue<T> {
  once: (trigger: callback<T>) => cleanup
}

interface _TriggerCache<T> extends _KeyedTrigger<T> {
  get: (key: key, defaulter?: (key: key)=>T) => T,
  set: (key: key, value?: T) => cleanup,
  add: (key: key, trigger: callback<T>, initialize?: indicator) => cleanup,
  single: (key: key, initialValue?: T) => TriggerValue<T>,
  use: (key: key, options?: useValueOptions<T>) => [T, (value?: T)=>void],
  as: <U,>(key: key, options?: useValueAsOptions<T,U>) => [U, (value?: T)=>void],
}
export interface TriggerCache<T> extends _TriggerCache<T> {
  once: (key: key, trigger: callback<T>) => cleanup
}

export interface TriggerStore extends TriggerCache<any> {
  get: <T>(key: key, defaulter?: (key: key)=>T) => T,
  single: <T>(key: key, initialValue?: T) => TriggerValue<T>,
}

const reportCallbackError = (error: unknown) => console.debug('Trigger callback failed', error instanceof Error ? error.message : String(error))
const simpleSubscriptions = new WeakMap<object, ReturnType<typeof createSubscriptions>>()
const keyedSubscriptions = new WeakMap<object, (key: key) => ReturnType<typeof createSubscriptions>>()
function _use<T>(trigger: Trigger<T> & TriggerValue<T>, options?: useOptions<T>) {
  // call function when trigger is triggered
  
  const callback = useR()
  const handler = typeof options === 'function' ? options : options?.add
  useLayoutEffect(() => { callback.current = handler })
  const optionsValueSet = options && Object.hasOwn(options, 'value')
  useLayoutEffect(() => {
    // only trigger immediately if no options.value
    if (!handler) return undefined
    return trigger.add((v, o) => {
      console.debug('trigger use')
      try { return callback.current?.(v, o) } catch (error) { reportCallbackError(error) }
    }, !optionsValueSet)
  }, [trigger, !!handler, optionsValueSet])
  // trigger when options value changes, including immediately after first render
  useE(trigger, optionsValueSet, options?.value, () => { if (optionsValueSet) trigger.trigger(options.value) })
}
function _useValue<T,U>(triggerValue: TriggerValue<T>, options?: _useValueOptions<T,U>): [U, (value: T)=>void] {
  // in addition to _use, also maintain state, and allow an 'as' transformation on the returned value
  const as = options?.as || pass
  const fallback = useM(triggerValue, () => options?.defaulter?.call(this))
  const [source, setSource] = useState(() => ({ owner: triggerValue, value: triggerValue.get() }))
  useLayoutEffect(() => triggerValue.add(() => {
    setSource({ owner: triggerValue, value: triggerValue.get() })
  }, true), [triggerValue])
  const raw = source.owner === triggerValue ? source.value : triggerValue.get()
  const value = as(raw ?? options?.default ?? fallback)
  _use(triggerValue, options)
  return [value, value => triggerValue.set(value)]
}
function _clean<T>(implicitValue: fields<T>) {
  if (typeof(implicitValue) !== 'object') return implicitValue
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { value, add, remove, trigger, use, get, set, single, current, _, ...cleaned } = implicitValue as anyFields
  return cleaned
}

const _generateSyntacticMethods = <T,U>(partialClass: T): T & { once: (trigger: callback<U>) => cleanup } => {
  const _tempClass = partialClass as any
  return Object.assign(_tempClass, {
    once: trigger => {
      const remove = _tempClass.add((...args) => {
        remove()
        trigger(...args)
      })
      return remove
    }
  })
}
const _generateKeyedSyntacticMethods = <T,U>(partialClass: T): T & { once: (key: key, trigger: callback<U>) => cleanup } => {
  const _tempClass = partialClass as any
  return Object.assign(_tempClass, {
    once: (key, trigger) => {
      const remove = _tempClass.add(key, (...args) => {
        remove()
        trigger(...args)
      })
      return remove
    }
  })
}

const _simple = <T,>({ name }:{ name?: string }={}): Trigger<T> => {
  const subscriptions = createSubscriptions<T>(reportCallbackError)
  const result = _generateSyntacticMethods<_Trigger<T>, T>({
    name,
    add: callback => subscriptions.add(callback),
    remove: subscriptions.remove,
    trigger: (value: T, oldValue?: T) => subscriptions.trigger(value, oldValue),
    use: function (options) { return _use(this, options) },
  })
  simpleSubscriptions.set(result, subscriptions)
  return result
}

const _keyed: <T>()=>KeyedTrigger<T> = <T,>() => {
  const triggerDict = new Map<string, ReturnType<typeof createSubscriptions<T>>>()
  const getSubscriptions = (key: key) => {
    const name = String(key)
    if (!triggerDict.has(name)) triggerDict.set(name, createSubscriptions<T>(reportCallbackError))
    return triggerDict.get(name)
  }
  const singles = Object.create(null)
  const result = _generateKeyedSyntacticMethods<_KeyedTrigger<T>, T>({
    add: (key, callback: callback<T>) => getSubscriptions(key).add(callback),
    remove: (key, callback: callback<T>) => triggerDict.get(String(key))?.remove(callback),
    trigger: (key, value: T, oldValue?: T) => {
      console.debug('trigger', String(key), triggerDict.get(String(key))?.size || 0)
      triggerDict.get(String(key))?.trigger(value, oldValue)
    },
    use: function (key, options) { return _use(this.single(key), options) },
    single: function (key) {
      return Object.load(singles, key, () => _generateSyntacticMethods<_Trigger<T>, T>({
        add: (...rest) => this.add(key, ...rest),
        remove: (...rest) => this.remove(key, ...rest),
        trigger: (...rest) => this.trigger(key, ...rest),
        use: (...rest) => this.use(key, ...rest),
      }))
    },
  })
  keyedSubscriptions.set(result, getSubscriptions)
  return result
}

const _cached = <T,>(defaulter?: (key:key)=>T) => {
  const _ = {
    defaulter,
    keyed: _keyed<T>(),
    values: Object.create(null),
    singles: Object.create(null),
  }
  return _generateKeyedSyntacticMethods<_TriggerCache<T>, T>(Object.assign({}, _.keyed, {
    _,
    current: _.values,
    get: function (key, defaulter=_.defaulter) {
      return Object.load(this._.values, key, defaulter)
    },
    set: function (key, value) {
      value = value ?? (defaulter && defaulter(key))
      const previous = this.get(key)
      if (previous !== value) {
        this._.values[key] = value
        this._.keyed.trigger(key, value, previous)
      }
      return () => {
        // only revert on cleanup if another value isn't immediately set
        // TODO design better way of chaining sets which allows complete ordered undo
        let triggered = false
        const remove = this.add(key, () => triggered = true)
        setTimeout(() => {
          remove()
          if (!triggered) this.set(key, previous)
        })
      }
    },
    trigger: function (key, value) {
      // intercept KeyedTrigger<T>.trigger to set value instead
      this.set(key, value)
    },
    add: function (key, callback, initialize=false) {
      return keyedSubscriptions.get(this._.keyed)(key).add(callback, initialize ? { value: this.get(key) } : undefined)
    },
    single: function (key, initialValue=undefined) {
      if (initialValue !== undefined) this.set(key, initialValue)
      return Object.load(this._.singles, key, () => Object.assign({}, this._.keyed.single(key), {
        get: (...rest) => this.get(key, ...rest),
        set: (...rest) => this.set(key, ...rest),
        trigger: (...rest) => this.trigger(key, ...rest),
        add: (...rest) => this.add(key, ...rest),
        use: (...rest) => this.use(key, ...rest),
        as: (...rest) => this.as(key, ...rest),
      }))
    },
    use: function (key, options) { return _useValue<T,T>(this.single(key), options) },
    as: function <U>(key, options) {
      if (typeof options === 'function') options = { as: options }
      return _useValue<T,U>(this.single(key), options)
    },
  }))
}

export const trigger: KeyedTrigger<unknown> & {
  new: <T>() => Trigger<T>,
  keyed: <T>() => KeyedTrigger<T>,
  value: <T>(initialValue?: T, options?: { name?: string }) => TriggerValue<T>,
  implicitOf: <T extends fields<any>, U extends TriggerValue<T>>(triggerValue: U) => T & U,
  implicit: <T extends fields<any>>(initialValue?: T) => T & TriggerValue<T>,
  cache: <T>(defaulter?: (key:key)=>T) => TriggerCache<T>,
  store: (defaulter?: (key:key)=>any) => TriggerStore,
} = Object.assign(_keyed<unknown>(), {
  new: _simple,
  keyed: _keyed,
  value: <T,>(initialValue?: T, options:{ name?: string }={}): TriggerValue<T> => {
    const _ = {
      trigger: _simple<T>(options),
      current: initialValue,
    }
    return _generateSyntacticMethods<_TriggerValue<T>, T>(Object.assign({
      get value() { return this.get() },
      
      _,
      get current() {
        return this._.current
      },
      set current(value) {
        if (this._.current !== value) {
          const previous = this._.current
          this._.current = value
          // console.debug(this._.current, value)
          this._.trigger.trigger(value, previous)
        }
      },
    }, _.trigger, {
      get: function () {
        return this.current
      },
      set: function (value) {
        // console.debug('SET TRIGGER VALUE', this.name, this.current, value)
        // if (this.current.token) {
        //   console.debug('[set auth trigger]', this.current.token, value.token)
        //   if (this.current.token && !value.token) {
        //     try {
        //       throw 'nope'
        //     } catch (e) {
        //       console.error('[set auth trigger] token removed', e)
        //     }
        //   }
        // }
        const previous = this.current
        this.current = value ?? initialValue
        return () => {
          // only revert on cleanup if another value isn't immediately set
          let triggered = false
          const remove = this.add(() => triggered = true)
          setTimeout(() => {
            remove()
            if (!triggered) this.current = previous
          }, 10)
        }
      },
      trigger: function (value) {
        // intercept Trigger<T>.trigger to set value instead, then setter will trigger
        this.set(value)
      },
      add: function (callback, initialize=false) {
        // console.debug('ADD', this.name, initialize, callback)
        return simpleSubscriptions.get(this._.trigger).add(callback, initialize ? { value: this.get() } : undefined)
      },
      use: function (options) { return _useValue<T,T>(this, options) },
      as: function <U>(options) {
        if (typeof options === 'function') options = { as: options }
        return _useValue<T,U>(this, options)
      },
    }))
  },
  implicitOf: triggerValue => {
    triggerValue.add(value => Object.assign(triggerValue, _clean(value)), true)
    return triggerValue
  },
  implicit: function (initialValue?) {
    return this.implicitOf(this.value(initialValue))
  },
  cache: _cached,
  store: _cached,
})
